import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faCheckCircle,
  faGift,
  faTrophy,
  faUsers,
  faHistory,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faSackDollar,
  faCoins,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Form,
  InputGroup,
} from "@themesberg/react-bootstrap";
import { adminGetBonusCycles, adminGetBonusSlabs, myBonusDashboard } from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.9rem",
  color: "#ffffff",
};

const INACTIVE_BUTTON_STYLE = {
  background: "#ffffff",
  border: "1px solid #d8e1f0",
  borderRadius: "0.9rem",
  color: "#1f2a44",
};

const DEFAULT_FILTERS = {
  cycle_id: "",
  slab_id: "",
  status: "",
  search: "",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "processed", label: "Processed" },
  { value: "failed", label: "Failed" },
  { value: "already claimed", label: "Already Claimed" },
];

const DEFAULT_PAGINATION = {
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
};

const badgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (["processed", "sent", "eligible", "active", "ready to process"].includes(s)) return "success";
  if (["pending", "draft"].includes(s)) return "warning";
  if (["failed", "inactive", "not eligible", "already claimed", "archived"].includes(s)) return "danger";
  return "secondary";
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const normalized = String(value).includes(" ") ? String(value).replace(" ", "T") : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};
const getShowingRange = (pagination, currentRowsCount = 0) => {
  const total = Number(pagination?.total || 0);
  const perPage = Number(pagination?.per_page || 10);
  const lastPage = Math.max(1, Number(pagination?.last_page || Math.ceil(total / perPage) || 1));
  const current = Math.min(Math.max(1, Number(pagination?.current_page || 1)), lastPage);

  if (total === 0 || currentRowsCount === 0) return { start: 0, end: 0 };

  const start = (current - 1) * perPage + 1;
  const end = Math.min(start + currentRowsCount - 1, total);

  return {
    start: Math.min(start, total),
    end: Math.max(Math.min(end, total), Math.min(start, total)),
  };
};

const toText = (value) => String(value ?? "").trim();

const mapCycleOption = (item) => ({
  id: item?.id ?? item?.cycle_id ?? "",
  cycle_name: item?.cycle_name || item?.name || "N/A",
  cycle_month: item?.cycle_month || "",
  status: item?.status || "",
});

const mapSlabOption = (item) => ({
  id: item?.id ?? item?.slab_id ?? "",
  slab_name: item?.slab_name || item?.name || "N/A",
  threshold: item?.threshold ?? "",
});


const StatCard = ({ icon, title, value }) => (
  <Card className="border-0 shadow-sm rounded-4 h-100 bonus-stat-card">
    <Card.Body className="p-3 p-md-4">
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="text-muted small mb-1">{title}</div>
          <div className="fw-bold fs-4">{value ?? "N/A"}</div>
        </div>
        <div className="bonus-stat-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
      </div>
    </Card.Body>
  </Card>
);

const getApiData = (response) => response?.data?.data || response?.data || {};

const getApiList = (response, keys = []) => {
  const data = getApiData(response);
  for (const key of keys) {
    const value = data?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

export default function UserMyBonus() {
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [activeView, setActiveView] = useState("eligible");

  const [cycleFilter, setCycleFilter] = useState("");
  const [slabFilter, setSlabFilter] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [eligiblePage, setEligiblePage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  const [cycleOptions, setCycleOptions] = useState([]);
  const [slabOptions, setSlabOptions] = useState([]);

  const [dashboard, setDashboard] = useState({
    followers_count: 0,
    highest_slab: "N/A" || "-",
    eligible_count: 0,
    claimed_count: 0,
    total_bonus_amount: 0,
    current_cycle_bonus_amount: 0,
    current_cycle: null,
    eligible_slabs: [],
    history: [],
    eligible_pagination: DEFAULT_PAGINATION,
    history_pagination: DEFAULT_PAGINATION,
  });

  const initialLoadRef = useRef(true);
  const searchDebounceRef = useRef(null);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const buildParams = useCallback((pageNo = 1, perPage = 10, filters = DEFAULT_FILTERS) => {
    const params = {
      page: pageNo,
      per_page: perPage,
    };

    const cycleId = toText(filters.cycle_id);
    const slabId = toText(filters.slab_id);
    const searchValue = toText(filters.search);
    const statusValue = toText(filters.status);

    if (cycleId) params.cycle_id = cycleId;
    if (slabId) params.slab_id = slabId;
    if (searchValue) params.search = searchValue;
    if (statusValue) params.status = statusValue;

    return params;
  }, []);

  const fetchCycleOptions = useCallback(async () => {
    try {
      const response = await adminGetBonusCycles({ pagination: false });
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCycles = Array.isArray(apiData?.cycles) ? apiData.cycles : [];
      setCycleOptions(apiCycles.map(mapCycleOption));
    } catch (error) {
      console.error("Failed to fetch cycle options:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch cycle options.");
      setCycleOptions([]);
    }
  }, []);

  const fetchSlabOptions = useCallback(async () => {
    try {
      const response = await adminGetBonusSlabs({ pagination: false });
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiSlabs = Array.isArray(apiData?.slabs) ? apiData.slabs : [];
      setSlabOptions(apiSlabs.map(mapSlabOption));
    } catch (error) {
      console.error("Failed to fetch slab options:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch slab options.");
      setSlabOptions([]);
    }
  }, []);

  const fetchDashboard = useCallback(
    async ({ pageNo = 1, perPage = 10, filters = DEFAULT_FILTERS } = {}) => {
      try {
        setLoading(true);

        const response = await myBonusDashboard(buildParams(pageNo, perPage, filters));
        const apiData = getApiData(response);

        const eligibleSlabsRaw = Array.isArray(apiData?.eligible_slabs)
          ? apiData.eligible_slabs
          : Array.isArray(apiData?.eligible)
            ? apiData.eligible
            : [];

        const historyRaw = Array.isArray(apiData?.history)
          ? apiData.history
          : Array.isArray(apiData?.bonus_history)
            ? apiData.bonus_history
            : [];

        setDashboard({
          followers_count: apiData?.followers_count ?? 0,
          highest_slab: apiData?.highest_slab ?? "N/A",
          eligible_count: apiData?.eligible_count ?? 0,
          claimed_count: apiData?.claimed_count ?? 0,
          total_bonus_amount: apiData?.total_bonus_amount ?? 0,
          current_cycle_bonus_amount: apiData?.current_cycle_bonus_amount ?? 0,
          current_cycle: apiData?.current_cycle ?? null,
          eligible_slabs: eligibleSlabsRaw,
          history: historyRaw,
          eligible_pagination:
            apiData?.eligible_pagination ||
            apiData?.eligiblePagination ||
            apiData?.pagination ||
            {
              current_page: pageNo,
              last_page: 1,
              per_page: perPage,
              total: eligibleSlabsRaw.length,
            },
          history_pagination:
            apiData?.history_pagination ||
            apiData?.historyPagination ||
            apiData?.pagination ||
            {
              current_page: pageNo,
              last_page: 1,
              per_page: perPage,
              total: historyRaw.length,
            },
        });
      } catch (error) {
        console.error("Failed to fetch bonus dashboard:", error);
        toast.error(error?.response?.data?.message || "Unable to load bonus dashboard.");
        setDashboard({
          followers_count: 0,
          highest_slab: "N/A",
          eligible_count: 0,
          claimed_count: 0,
          current_cycle: null,
          eligible_slabs: [],
          history: [],
          eligible_pagination: DEFAULT_PAGINATION,
          history_pagination: DEFAULT_PAGINATION,
        });
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    (async () => {
      setLoadingOptions(true);
      try {
        await Promise.all([fetchCycleOptions(), fetchSlabOptions()]);
      } finally {
        setLoadingOptions(false);
      }
    })();
  }, [fetchCycleOptions, fetchSlabOptions]);

  useEffect(() => {
    fetchDashboard({
      pageNo: 1,
      perPage: 10,
      filters: DEFAULT_FILTERS,
    });
  }, [fetchDashboard]);

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return undefined;
    }

    const currentPage = activeView === "eligible" ? eligiblePage : historyPage;
    const perPage =
      activeView === "eligible"
        ? dashboard.eligible_pagination?.per_page || 10
        : dashboard.history_pagination?.per_page || 10;

    clearSearchDebounce();
    const delay = String(search || "").trim() ? 300 : 0;

    searchDebounceRef.current = setTimeout(() => {
      fetchDashboard({
        pageNo: currentPage,
        perPage,
        filters: {
          cycle_id: cycleFilter,
          slab_id: slabFilter,
          status: activeView === "history" ? status : "",
          search,
        },
      });
    }, delay);

    return () => clearSearchDebounce();
  }, [
    activeView,
    eligiblePage,
    historyPage,
    cycleFilter,
    slabFilter,
    status,
    search,
    dashboard.eligible_pagination?.per_page,
    dashboard.history_pagination?.per_page,
    fetchDashboard,
  ]);

  const handleCycleChange = (value) => {
    setCycleFilter(value);
    setEligiblePage(1);
    setHistoryPage(1);
  };

  const handleSlabChange = (value) => {
    setSlabFilter(value);
    setEligiblePage(1);
    setHistoryPage(1);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setEligiblePage(1);
    setHistoryPage(1);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setHistoryPage(1);
    setEligiblePage(1);
  };

  const handleClearFilters = () => {
    setCycleFilter("");
    setSlabFilter("");
    setSearch("");
    setStatus("");
    setEligiblePage(1);
    setHistoryPage(1);
  };

  const stats = useMemo(
    () => [
      { title: "Followers Count", value: dashboard.followers_count, icon: faUsers },
      { title: "Highest Slab", value: dashboard.highest_slab, icon: faTrophy },
      { title: "Eligible Count", value: dashboard.eligible_count, icon: faCheckCircle },
      { title: "Claimed Count", value: dashboard.claimed_count, icon: faGift },
      { title: "Current Cycle", value: dashboard.current_cycle?.cycle_name || "N/A", icon: faCalendarDays },
      { title: "Total Bonus Amount", value: dashboard.total_bonus_amount || "N/A", icon: faCoins },
      // { title: "Current Cycle Bonus", value: dashboard.current_cycle_bonus_amount || "N/A", icon: faSackDollar },
    ],
    [dashboard]
  );

  const eligiblePagination = dashboard.eligible_pagination || DEFAULT_PAGINATION;
  const historyPagination = dashboard.history_pagination || DEFAULT_PAGINATION;

  const eligibleRows = dashboard.eligible_slabs || [];
  const historyRows = dashboard.history || [];

  const activePagination = activeView === "eligible" ? eligiblePagination : historyPagination;
  const activePage = activeView === "eligible" ? eligiblePage : historyPage;
  const activeRows = activeView === "eligible" ? eligibleRows : historyRows;
  const showingRange = getShowingRange(activePagination, activeRows.length);
  const pageItems = getPageItems(activePagination.current_page || 1, activePagination.last_page || 1);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-4">
        <div>
          <h2 className="h4 mb-1">My Bonus</h2>
          {/* <div className="text-muted">View your bonus eligibility and claim history.</div> */}
        </div>

        <div className="d-flex justify-content-end ms-auto bonus-toggle-group">
          <Button
            type="button"
            style={activeView === "eligible" ? ACTIVE_BUTTON_STYLE : INACTIVE_BUTTON_STYLE}
            className="fw-semibold bonus-toggle-btn"
            onClick={() => setActiveView("eligible")}
          >
            <FontAwesomeIcon icon={faTrophy} className="me-2" />
            Eligible
          </Button>

          <Button
            type="button"
            style={activeView === "history" ? ACTIVE_BUTTON_STYLE : INACTIVE_BUTTON_STYLE}
            className="fw-semibold bonus-toggle-btn"
            onClick={() => setActiveView("history")}
          >
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            History
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        {stats.map((item, index) => (
          // <Col key={item.title} xs={12} sm={6} xl={index < 3 ? 4 : 6}>
          <Col key={item.title} xs={12} sm={6} xl={4}>
            <StatCard icon={item.icon} title={item.title} value={item.value} />
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={activeView === "history" ? 3 : 4}>
              <Form.Label className="fw-semibold">Cycle</Form.Label>
              <Form.Select
                value={cycleFilter}
                onChange={(e) => handleCycleChange(e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">All Cycles</option>
                {cycleOptions.map((item) => (
                  <option key={String(item.id)} value={item.id}>
                    {item.cycle_name}
                    {item.cycle_month ? ` (${item.cycle_month})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={activeView === "history" ? 3 : 4}>
              <Form.Label className="fw-semibold">Slab</Form.Label>
              <Form.Select
                value={slabFilter}
                onChange={(e) => handleSlabChange(e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">All Slabs</option>
                {slabOptions.map((item) => (
                  <option key={String(item.id)} value={item.id}>
                    {item.slab_name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {activeView === "history" ? (
              <Col xs={12} md={3}>
                <Form.Label className="fw-semibold">Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={loading}
                >
                  {STATUS_OPTIONS.map((item) => (
                    <option key={item.value || "all"} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : null}

            <Col xs={12} md={activeView === "history" ? 3 : 4}>
              <Form.Label className="fw-semibold">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={activeView === "history" ? "Search" : "Search"}
                />
              </InputGroup>
            </Col>
          </Row>

          {/* <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
            <div className="text-muted small">
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Filters applied on {activeView === "eligible" ? "eligible slabs" : "bonus history"}
            </div>

            <Button variant="outline-primary" onClick={handleClearFilters} className="fw-semibold">
              Clear Filters
            </Button>
          </div> */}
        </Card.Body>
      </Card>

      {activeView === "eligible" ? (
        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Eligible Slabs</h5>
                <div className="text-muted small">
                  {dashboard.current_cycle?.cycle_name
                    ? `Current cycle: ${dashboard.current_cycle.cycle_name}`
                    : "No current cycle available."}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center text-muted py-5">Loading eligible slabs...</div>
            ) : eligibleRows.length > 0 ? (
              <Row className="g-3">
                {eligibleRows.map((item, index) => (
                  <Col key={item.id ?? `${item.slab_name || "eligible"}-${index}`} xs={12} md={6} xl={4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 bonus-item-card">
                      <Card.Body className="p-3 p-md-4">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                          <div>
                            <div className="text-muted small mb-1">Slab Name</div>
                            <div className="fw-bold fs-5">{item.slab_name || "N/A" || "-"}</div>
                          </div>
                          <Badge bg="success" className="rounded-pill px-3 py-2">
                            Eligible
                          </Badge>
                        </div>

                        <div className="mb-2">
                          <span className="text-muted small">Threshold: </span>
                          <span className="fw-semibold">{item.threshold ?? "N/A"}</span>
                        </div>

                        <div>
                          <div className="text-muted small mb-1">Gift Description</div>
                          <div className="fw-medium">{item.gift_description || "No gift description available."}</div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center text-muted py-5">No eligible slabs found.</div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {showingRange.start} to {showingRange.end} of {activePagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={activePagination.current_page <= 1 || loading}
                  onClick={() => setEligiblePage(Math.max(1, activePagination.current_page - 1))}
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {pageItems.map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() => setEligiblePage(item)}
                      className={`course-page-btn ${item === activePagination.current_page ? "course-page-btn-active" : ""
                        }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={activePagination.current_page >= activePagination.last_page || loading}
                  onClick={() =>
                    setEligiblePage(Math.min(activePagination.last_page, activePagination.current_page + 1))
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Bonus History</h5>
                <div className="text-muted small">Your processed bonus records are shown below.</div>
              </div>
            </div>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0 bonus-table">
                <thead>
                  <tr>
                    <th>Slab</th>
                    <th>Cycle</th>
                    <th>Followers</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    {/* <th>Processed At</th> */}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        Loading bonus history...
                      </td>
                    </tr>
                  ) : historyRows.length > 0 ? (
                    historyRows.map((item, index) => (
                      <tr key={item.id ?? `${item.cycle || "history"}-${index}`}>
                        <td className="fw-semibold">{item.slab || item.slab_name || "N/A" || "-"}</td>
                        <td>{item.cycle || item.cycle_name || "N/A" || "-"}</td>
                        <td>{item.followers_count ?? "N/A"}</td>
                        <td>
                          <Badge
                            bg={badgeVariant(item.processing_status)}
                            className="rounded-pill px-3 py-2 text-capitalize"
                          >
                            {item.processing_status || "N/A"}
                          </Badge>
                        </td>
                        <td>{item.remarks || "N/A"}</td>
                        {/* <td>{formatDateTime(item.processed_at)}</td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        No bonus history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {showingRange.start} to {showingRange.end} of {activePagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={activePagination.current_page <= 1 || loading}
                  onClick={() => setHistoryPage(Math.max(1, activePagination.current_page - 1))}
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {pageItems.map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() => setHistoryPage(item)}
                      className={`course-page-btn ${item === activePagination.current_page ? "course-page-btn-active" : ""
                        }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={activePagination.current_page >= activePagination.last_page || loading}
                  onClick={() =>
                    setHistoryPage(Math.min(activePagination.last_page, activePagination.current_page + 1))
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <style>{`
        .bonus-toggle-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: nowrap;
  }

  .bonus-toggle-btn {
    min-width: 9.5rem;
    padding: 0.85rem 1.25rem;
    box-shadow: none !important;
    white-space: nowrap;
  }

        .bonus-stat-card,
        .bonus-item-card {
          border-radius: 1.25rem !important;
        }

        .bonus-stat-icon {
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #eef4ff;
          color: #1f5fd4;
          flex: 0 0 auto;
        }

        .bonus-table thead th {
          white-space: nowrap;
        }

        @media (max-width: 767.98px) {
    .bonus-toggle-group {
      width: auto;
      flex-wrap: nowrap;
    }

    .bonus-toggle-btn {
      width: auto;
      min-width: 0;
      flex: 1 1 0;
      padding: 0.75rem 1rem;
    }
  }
      `}</style>
    </>
  );
}